/**
 * Waterfall Chart Component
 *
 * Implements the standard "stacked invisible baseline + visible delta" trick
 * with recharts. Backend resolver `bar.resolve_waterfall` already adds
 * `delta` and `cumulative` per row, so we derive each bar as:
 *   - baseline (invisible) = cumulative - max(delta, 0)
 *   - positive bar = delta when delta >= 0
 *   - negative bar = |delta| when delta < 0
 *
 * Up bars are green, down bars are red.
 */

import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useIndicatorData } from './useIndicatorData';
import { generateMockData, MockBarData } from '../utils';

interface WaterfallComponentProps {
  indicatorId?: number;
  height?: number | string;
}

interface WaterfallRow {
  name: string;
  baseline: number;
  positive: number;
  negative: number;
  cumulative: number;
  delta: number;
}

const POSITIVE_COLOR = '#10b981';
const NEGATIVE_COLOR = '#ef4444';

export function WaterfallComponent({ indicatorId, height = 250 }: WaterfallComponentProps = {}) {
  const { data: live, loading } = useIndicatorData(indicatorId);
  const isPreviewMode = indicatorId === undefined;

  const rows: WaterfallRow[] = useMemo(() => {
    const sourceRows = live?.data && live.data.length > 0
      ? live.data
      : isPreviewMode
        ? (generateMockData('waterfall') as MockBarData[]).map((m, idx, arr) => ({
            name: m.name,
            value: m.value,
            delta: m.value,
            cumulative: arr.slice(0, idx + 1).reduce((acc, r) => acc + r.value, 0),
          }))
        : [];
    if (sourceRows.length === 0) return [];

    return sourceRows.map((r) => {
      const delta = typeof r.delta === 'number' ? r.delta : Number(r.value || 0);
      const cumulative = typeof r.cumulative === 'number' ? r.cumulative : Number(r.value || 0);
      const baseline = delta >= 0 ? cumulative - delta : cumulative;
      return {
        name: String(r.name),
        baseline: Math.max(baseline, 0),
        positive: delta >= 0 ? delta : 0,
        negative: delta < 0 ? Math.abs(delta) : 0,
        cumulative,
        delta,
      };
    });
  }, [live, isPreviewMode]);

  if (rows.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-xs text-muted-foreground"
        style={{ height: typeof height === 'number' ? `${height}px` : height }}
      >
        {loading ? 'Loading…' : (live?.message || 'No data. Configure axes in the builder.')}
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={rows} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" fontSize={12} interval={0} angle={-25} textAnchor="end" height={90} tickMargin={6} />
        <YAxis fontSize={12} />
        <Tooltip
          formatter={(_v, _n, item) => {
            const datum = item.payload as WaterfallRow | undefined;
            if (!datum) return '';
            return [`Δ ${datum.delta} (cum ${datum.cumulative})`, 'Change'];
          }}
        />
        <Legend verticalAlign="top" height={28} wrapperStyle={{ paddingBottom: 4 }} />
        {/* Invisible baseline keeps the visible bar at the right offset */}
        <Bar dataKey="baseline" stackId="wf" fill="transparent" name="" legendType="none" />
        <Bar dataKey="positive" stackId="wf" name="Increase">
          {rows.map((_row, idx) => (
            <Cell key={`pos-${idx}`} fill={POSITIVE_COLOR} />
          ))}
        </Bar>
        <Bar dataKey="negative" stackId="wf" name="Decrease">
          {rows.map((_row, idx) => (
            <Cell key={`neg-${idx}`} fill={NEGATIVE_COLOR} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
